import MapBaseLayer from '@alltrails/amplitude/enums/MapBaseLayer';
import MapDrawerLocation from '@alltrails/amplitude/enums/MapDrawerLocation';
import MapLocation from '@alltrails/amplitude/enums/MapLocation';
import MapOverlay from '@alltrails/amplitude/enums/MapOverlay';
import { advancedBuilderPath } from '@alltrails/layout/utils/customRouteBuilderPaths';
import { baseMapStyles, conditionsStyles, extraStyles } from '../components/LayerMenu/layerMenuStyles';
import { BaseStyleId, DynamicStyleId, StaticStyleId } from '../types/Styles';
import { getOverlayOpacityPercent, type OverlayOpacities } from './overlayOpacity';
import type { StackableOverlayId } from './overlayStack';

export const getMapBaseLayer = (styleId: BaseStyleId): MapBaseLayer | undefined => {
  switch (styleId) {
    case 'customBasemap':
      return 'custom';
    case 'alltrailsOutdoorsV2':
    case 'alltrailsOutdoorsV3':
      return 'alltrailsv2';
    case 'alltrailsSatelliteV2':
    case 'alltrailsSatelliteMinimal':
      return 'satellite';
    case 'googleSatellite':
      return 'google satellite';
    case 'opencycle':
      return 'ocm';
    case 'openstreet':
      return 'osm';
    case 'roadmap':
      return 'road';
    case 'topo':
      return 'usgs';
    case 'worldparks':
      return 'terrain';
    case 'worldtopo':
      return 'worldtopo';
    case 'osGreatBritain':
      return 'british os';
    case 'ignPlan':
      return 'ign plan';
    case 'ignScan':
      return 'ign scan 25';
    default:
      return undefined;
  }
};

export const getMapOverlay = (styleId: StaticStyleId | DynamicStyleId): MapOverlay | undefined => {
  switch (styleId) {
    case 'customOverlay':
      return 'custom overlay';
    case 'after':
      return 'recordings last 60 days';
    case 'communityActivity':
      return 'community activity';
    case 'communityWaypoints':
      return 'community waypoints';
    case 'fallFoliage':
      return 'fall foliage';
    case 'geoBoundaries':
      return 'polygons';
    case 'groundConditions':
      return 'ground conditions';
    case 'heatmap':
      return 'heatmap';
    case 'highDefTerrain':
      return 'lidar hillshade';
    case 'limit':
      return 'most recent 50';
    case 'manageTrailheads':
      return 'admin trailheads';
    case 'mapsAll':
      return 'maps all';
    case 'nearbyTrails':
      return 'nearby_trails';
    case 'newHeatmap':
      return 'heatmap';
    case 'pendingTrails':
      return 'pending trails';
    case 'personalHeatmap':
      return 'personal heatmap';
    case 'photoHeatmap':
      return 'photo hotspots';
    case 'photos':
      return 'photos';
    case 'photospot':
      return 'photospots';
    case 'precipitation':
      return 'precipitation';
    case 'privateLands':
      return 'private lands';
    case 'publicLands':
      return 'public lands';
    case 'recordingEnd':
      return 'activity ends';
    case 'recordingStart':
      return 'activity starts';
    case 'recordingsAll':
      return 'recordings all';
    case 'recordingsPop':
      return 'recordings popular';
    case 'savedRoutePolyline':
      return 'display original polyline';
    case 'slopeAngle':
      return 'slope angle';
    case 'snowDepth':
      return 'snow-depth';
    case 'surfaceType':
      return 'surface';
    case 'temperature':
      return 'temperature';
    case 'userWaypoints':
      return 'your waypoints';
    case 'waymarkedCycling':
      return 'waymarked cycling';
    case 'waymarkedHiking':
      return 'waymarked hiking';
    case 'waymarkedMTB':
      return 'waymarked mountain biking';
    case 'waypoints':
      return 'waypoints';
    case 'wildfire':
      return 'wildfires';
    default:
      return undefined;
  }
};

export const getPage = (pathname: string): MapLocation => {
  // Before the basic builder's /custom-routes and the /trail of /adv/custom-routes/trail/:slug, which it also contains
  if (pathname.includes(advancedBuilderPath)) {
    return 'advanced custom route builder';
  }
  if (pathname.includes('/custom-routes')) {
    return 'custom route';
  }
  if (pathname.includes('/explore')) {
    return 'explore';
  }
  if (pathname.includes('/trail')) {
    return 'trail';
  }

  return '' as MapLocation;
};

// `custom route saved` and `custom route updated` take only these two, telling the paid advanced builder from the free one
export const getCustomRouteBuilderMapLocation = (pathname: string): MapLocation =>
  pathname.includes(advancedBuilderPath) ? 'advanced custom route builder' : 'custom route';

export const getMapDrawerLocation = (styleId: StaticStyleId | DynamicStyleId): MapDrawerLocation => {
  if (baseMapStyles.includes(styleId as BaseStyleId)) {
    return 'base map';
  }
  if (conditionsStyles.includes(styleId as StaticStyleId)) {
    return 'layers';
  }
  if (extraStyles.includes(styleId as StaticStyleId) || styleId === 'communityActivity') {
    return 'details';
  }
  return 'layers';
};

export const getOverlayStackAnalytics = (stackedOverlayIds: StackableOverlayId[], overlayOpacities: OverlayOpacities) => {
  const topFirst = [...stackedOverlayIds].reverse();
  const primaryId = topFirst[0];
  return {
    applied_overlays: topFirst.map(getMapOverlay).filter((overlay): overlay is MapOverlay => Boolean(overlay)),
    applied_overlay_count: topFirst.length,
    primary_layer: primaryId ? getMapOverlay(primaryId) : undefined,
    primary_layer_opacity: primaryId ? getOverlayOpacityPercent(overlayOpacities, primaryId) : undefined
  };
};

export const getOverlayStackEventProps = (
  stackedOverlayIds: StackableOverlayId[],
  overlayOpacities: OverlayOpacities,
  styleId: StackableOverlayId,
  trailId?: number
) => {
  const { applied_overlays, primary_layer, primary_layer_opacity } = getOverlayStackAnalytics(stackedOverlayIds, overlayOpacities);
  return {
    applied_overlays,
    map_overlay: getMapOverlay(styleId) ?? ('' as MapOverlay),
    primary_layer: primary_layer ?? ('' as MapOverlay),
    primary_layer_opacity: primary_layer_opacity ?? 0,
    trail_id: trailId ? String(trailId) : undefined
  };
};
